// Libs
import { useState, useRef } from "react";
import { toastHandler } from "@/lib/ToastHandler";

// Types
import { Driver } from "@/types/supabase";

// Hooks
import { useDriversApi } from "./useDriversApi";
import { useAuth } from "@/hooks/useAuth";
import { uploadDriverPicture } from "@/lib/api";
import { API_BASE_URL } from "@/lib/constants";

interface UseDriverImageUploadProps {
  driver: Driver;
  setDriver: (driver: Driver) => void;
  isEditing: boolean;
  onSaveFormData?: () => void;
}

// Added debugging logs to identify issues at critical points
export function useDriverImageUpload({
  driver,
  setDriver,
  isEditing,
  onSaveFormData,
}: UseDriverImageUploadProps) {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { updateDriver } = useDriversApi();
  const { getAccessToken } = useAuth();

  // Computed image source with proper fallback logic
  const displayImageSrc =
    imagePreview ||
    driver.picture ||
    "/driver-illustration/profile-pic-default.svg";

  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        // Debug: log dimensions
        console.debug(
          `validateImageDimensions: loaded image: width=${img.width} height=${img.height}`,
        );
        // Check minimum dimensions (50x50 pixels)
        if (img.width < 50 || img.height < 50) {
          toastHandler({
            title: "Imagen muy pequeña",
            description: "La imagen debe tener al menos 50x50 píxeles",
            variant: "destructive",
          });
          resolve(false);
          return;
        }

        // Check maximum dimensions (4000x4000 pixels)
        if (img.width > 4000 || img.height > 4000) {
          toastHandler({
            title: "Imagen muy grande",
            description: "La imagen no debe exceder 4000x4000 píxeles",
            variant: "destructive",
          });
          resolve(false);
          return;
        }

        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        console.error(
          "validateImageDimensions: Error loading the image, possibly corrupt.",
        );
        toastHandler({
          title: "Imagen corrupta",
          description:
            "No se pudo cargar la imagen. Por favor selecciona otra imagen.",
          variant: "destructive",
        });
        resolve(false);
      };

      img.src = objectUrl;
    });
  };

  const optimizeImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      let objectUrl: string | null = null;

      const cleanup = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };

      img.onload = () => {
        cleanup();

        // Calculate optimal dimensions (max 800x800 while maintaining aspect ratio)
        const maxSize = 800;
        let { width, height } = img;
        // Debug: log source dimensions
        console.debug(`optimizeImage: original dimensions: ${width}x${height}`);

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const baseName = file.name.split(".")[0] || "image";
              const optimizedFile = new File([blob], `${baseName}.jpg`, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              // Debug: log optimized file info
              console.debug(
                "optimizeImage: returning optimized file",
                optimizedFile,
              );
              resolve(optimizedFile);
            } else {
              console.warn(
                "optimizeImage: failed to create blob, using original file",
              );
              resolve(file);
            }
          },
          "image/jpeg",
          0.85,
        );
      };

      img.onerror = () => {
        cleanup();
        console.error("optimizeImage: Failed to load image for optimization.");
        resolve(file);
      };

      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    });
  };

  // Previous image lifecycle management is handled by the backend now.

  const validateAndProcessImage = async (file: File) => {
    // Debug: file details
    console.debug("validateAndProcessImage: Received file:", file);

    // Create preview URL immediately for instant visual feedback
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    // Save updated form state when editing
    if (isEditing) {
      onSaveFormData?.();
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
      "image/bmp",
      "image/tiff",
      "image/svg+xml",
    ];

    if (!allowedTypes.includes(file.type)) {
      setImagePreview(null);
      URL.revokeObjectURL(previewUrl);
      setSelectedImageFile(null);
      toastHandler({
        title: "Tipo de archivo no válido",
        description:
          "Por favor selecciona una imagen válida (JPG, PNG, WebP, GIF, BMP, TIFF, SVG)",
        variant: "destructive",
      });
      console.warn("validateAndProcessImage: Invalid file type", file.type);
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setImagePreview(null);
      URL.revokeObjectURL(previewUrl);
      setSelectedImageFile(null);
      toastHandler({
        title: "Archivo muy grande",
        description: "La imagen debe ser menor a 5MB",
        variant: "destructive",
      });
      console.warn("validateAndProcessImage: File too large", file.size);
      return;
    }

    // Validate minimum file size (1KB)
    if (file.size < 1024) {
      setImagePreview(null);
      URL.revokeObjectURL(previewUrl);
      setSelectedImageFile(null);
      toastHandler({
        title: "Archivo muy pequeño",
        description:
          "La imagen debe ser mayor a 1KB. Por favor selecciona una imagen válida.",
        variant: "destructive",
      });
      console.warn("validateAndProcessImage: File too small", file.size);
      return;
    }

    try {
      setIsProcessingImage(true);

      const isValidImage = await validateImageDimensions(file);
      if (!isValidImage) {
        setImagePreview(null);
        URL.revokeObjectURL(previewUrl);
        setSelectedImageFile(null);
        setIsProcessingImage(false);
        return;
      }

      setSelectedImageFile(file);
      console.debug("validateAndProcessImage: Image valid and selected");
      toastHandler({
        title: "Imagen seleccionada",
        description: "La imagen se subirá cuando guardes los cambios",
        variant: "default",
      });
    } catch (error) {
      console.error("validateAndProcessImage: Error validating image:", error);
      setImagePreview(
        driver.picture || "/driver-illustration/profile-pic-default.svg",
      );
      URL.revokeObjectURL(previewUrl);
      setSelectedImageFile(null);
      toastHandler({
        title: "Error de validación",
        description: "No se pudo validar la imagen. Intenta con otra imagen.",
        variant: "destructive",
      });
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.debug("handleFileChange: file selected", file);
      validateAndProcessImage(file);
    } else {
      console.debug("handleFileChange: no file selected");
    }
    event.target.value = "";
  };

  const uploadImage = async (): Promise<void> => {
    if (!selectedImageFile) {
      console.debug("uploadImage: no file selected for upload");
      return;
    }

    if (!driver.id) {
      console.error("uploadImage: No driver ID for upload");
      throw new Error("Driver ID is required for image upload");
    }

    setIsUploadingImage(true);
    console.debug("uploadImage: starting image upload...");

    try {
      // Optimize image before upload
      const optimizedFile = await optimizeImage(selectedImageFile);

      // Auth
      const token = await getAccessToken();

      // Ensure driverId is a number for the endpoint signature
      const driverIdNum =
        typeof driver.id === "string" ? parseInt(driver.id, 10) : driver.id;
      if (!Number.isFinite(driverIdNum as number)) {
        throw new Error("Driver ID inválido para la carga de imagen");
      }

      const uploadResponse = await uploadDriverPicture({
        apiBaseUrl: API_BASE_URL,
        driverId: driverIdNum as number,
        file: optimizedFile,
        token,
      });

      // Debug: Upload response logging
      console.debug("uploadImage: uploadResponse", uploadResponse);

      // Determine new picture URL from response (common shapes)
      const newPictureUrl =
        uploadResponse?.picture ||
        uploadResponse?.data?.picture ||
        uploadResponse?.url ||
        uploadResponse?.data?.url ||
        null;

      // If backend didn't return URL, try to refresh driver via API
      let finalUrl = newPictureUrl as string | null;
      if (!finalUrl) {
        try {
          // Debug: fallback to fetching updated driver
          console.debug(
            "uploadImage: No picture url from uploadResponse, refetching driver...",
          );
          const updated = await updateDriver(driver.id, {});
          finalUrl =
            (updated as any)?.picture ||
            (updated as any)?.data?.picture ||
            null;
        } catch (refreshError) {
          console.warn("uploadImage: Error on fallback refetch", refreshError);
        }
      }

      // Update local state
      if (finalUrl) {
        setDriver({ ...driver, picture: finalUrl });
        console.debug("uploadImage: setDriver with new picture", finalUrl);
      } else {
        console.warn(
          "uploadImage: No finalUrl after trying upload and updateDriver fallback",
        );
      }

      toastHandler({
        title: "Imagen actualizada",
        description: "La imagen del conductor se ha actualizado correctamente",
        variant: "success",
      });

      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(finalUrl || imagePreview);
      setSelectedImageFile(null);
    } catch (error) {
      console.error("uploadImage: Error in uploadImage process:", error);

      // Reset preview to original or default
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(
        driver.picture || "/driver-illustration/profile-pic-default.svg",
      );

      // Show detailed error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error desconocido al subir imagen";
      toastHandler({
        title: "Error al subir imagen",
        description: errorMessage,
        variant: "destructive",
      });

      throw error;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const resetImageSelection = () => {
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedImageFile(null);
    console.debug("resetImageSelection: image selection reset");
  };

  return {
    displayImageSrc,
    isUploadingImage,
    isProcessingImage,
    selectedImageFile,
    uploadImage,
    fileInputRef,
    handleFileChange,
    resetImageSelection,
  };
}
